import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DT } from '../theme/designTokens';
import { apiClient } from '../network/ApiClient';
import { AppSoundManager } from '../audio/AppSoundManager';
import { useTranslation } from '../i18n/react';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { ASSETS } from '../config/assets';

export function PlayAIScreen() {
  const navigate  = useNavigate();
  const { t } = useTranslation('play');
  const { t: tc } = useTranslation('common');
  // Error is a boolean flag: the only failure copy is frontend-owned and is
  // resolved at render, so it re-renders in the new language on a switch.
  const [failed, setFailed] = useState(false);
  // Prevents React StrictMode double-mount from firing two createBotSession calls.
  // The ref persists across StrictMode's fake unmount/remount; the retry button
  // calls startGame() directly so it is not affected by this guard.
  const firedRef = useRef(false);

  const startGame = useCallback(() => {
    setFailed(false);
    apiClient.createBotSession('medium')
      .then(result => {
        AppSoundManager.play('accept');
        navigate(`/game/${result.game_id}`, { state: { sessionId: result.session_id }, replace: true });
      })
      .catch(() => {
        AppSoundManager.play('deny');
        setFailed(true);
      });
  }, [navigate]);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    startGame();
  }, [startGame]);

  return (
    <div
      style={{
        position:       'relative',
        flex:           1,
        minHeight:      0,
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        gap:            28,
        padding:        '40px 24px',
        background:     DT.color.pageBg,
        overflow:       'hidden',
      }}
    >
      <AtmosphericGlow width={420} height={420} top="50%" opacity={0.22} />

      <img
        src={ASSETS.brand.LOGO_STACKED}
        alt="The Domino Game"
        style={{ position: 'relative', zIndex: 1, width: 132, height: 'auto' }}
      />

      {failed ? (
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div role="alert" style={{ fontSize: 15, color: DT.color.error, fontFamily: DT.font.sans, textAlign: 'center' }}>{t('ai.error')}</div>
          <button
            onClick={startGame}
            style={{
              padding:      '10px 24px',
              background:   DT.gradient.gold,
              color:        DT.color.goldOnGold,
              border:       'none',
              borderRadius: DT.radius.button,
              fontSize:     14,
              fontWeight:   700,
              fontFamily:   DT.font.sans,
              cursor:       'pointer',
              boxShadow:    '0 2px 12px rgba(201,168,76,0.22)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {tc('action.retry')}
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width:        200,
              height:       4,
              borderRadius: 999,
              background:   'rgba(201,168,76,0.15)',
              overflow:     'hidden',
              position:     'relative',
            }}
          >
            <div
              style={{
                position:     'absolute',
                top:          0,
                bottom:       0,
                width:        '40%',
                borderRadius: 999,
                background:   DT.gradient.gold,
                animation:    'loading-slide 1.3s ease-in-out infinite',
              }}
            />
          </div>
          <div role="status" style={{ fontSize: 14, color: DT.color.textMuted, fontFamily: DT.font.sans, letterSpacing: '0.02em' }}>
            {t('ai.starting')}
          </div>
        </div>
      )}
    </div>
  );
}
